/*
 * NARBASE TECHNOLOGIES CONFIDENTIAL
 * ______________________________
 * [2017] - [2026] Narbase Technologies
 * All Rights Reserved.
 * Created by shalaga44
 * On: 06/Oct/2026.
 */

package com.narbase.letsgo.web.utils.views

import com.narbase.kunafa.core.components.*
import com.narbase.kunafa.core.components.layout.LinearLayout
import com.narbase.kunafa.core.css.*
import com.narbase.kunafa.core.dimensions.dependent.wrapContent
import com.narbase.kunafa.core.dimensions.dimen
import com.narbase.kunafa.core.dimensions.px
import com.narbase.kunafa.core.drawable.Color
import com.narbase.kunafa.core.lifecycle.LifecycleOwner
import com.narbase.letsgo.web.common.AppColors
import com.narbase.letsgo.web.network.ServerCaller
import com.narbase.letsgo.web.network.makeNotVisible
import com.narbase.letsgo.web.network.makeVisible
import com.narbase.letsgo.web.translations.localized
import org.w3c.dom.HTMLInputElement
import org.w3c.files.get

class AudioUploader(private val defaultAudioUrl: String?) : Component() {

    private val uploaderViewController = ImageUploaderViewController()

    val audioUrl get() = uploaderViewController.imageUrl

    private var uploadButton: LinearLayout? = null
    private var uploadInput: Input? = null
    private var progressBar: ImageView? = null
    private var uploadedView: LinearLayout? = null
    private var player: Audio? = null

    override fun onViewCreated(lifecycleOwner: LifecycleOwner) {
        super.onViewCreated(lifecycleOwner)
        defaultAudioUrl?.takeIf { it.isNotBlank() }?.let { uploaderViewController.setHasImage(it) }
    }

    override fun onViewMounted(lifecycleOwner: LifecycleOwner) {
        uploaderViewController.imageUiState.observe {
            onAudioStateChanged(it ?: ImageUploaderViewController.UploadedImageState.Clear)
        }
    }

    private fun onAudioStateChanged(state: ImageUploaderViewController.UploadedImageState) {
        makeNotVisible(uploadButton, progressBar, uploadedView)
        when (state) {
            ImageUploaderViewController.UploadedImageState.Clear -> {
                uploadInput?.element?.value = ""
                player?.src = ""
                makeVisible(uploadButton)
            }

            ImageUploaderViewController.UploadedImageState.Uploading -> makeVisible(progressBar)
            ImageUploaderViewController.UploadedImageState.Uploaded -> {
                val url = audioUrl.orEmpty()
                player?.src = if (url.startsWith("http")) url else "${ServerCaller.BASE_URL}$url"
                makeVisible(uploadedView)
            }
        }
    }

    override fun View?.getView() = verticalLayout {
        style { width = wrapContent }

        uploadButton = horizontalLayout {
            style {
                width = wrapContent
                padding = "2px 20px".dimen()
                border = "1px solid ${AppColors.borderColorHex}"
                borderRadius = 18.px
                color = AppColors.text
                alignItems = Alignment.Center
                pointerCursor()
            }
            materialIcon("add") {
                style { marginEnd = 8.px }
            }
            textView {
                text = "Click to add audio".localized()
            }
            uploadInput = input {
                isVisible = false
                type = "file"
                element.accept = "audio/*"
                element.onchange = { event ->
                    val selectedAudio = (event.target as HTMLInputElement).files?.get(0)
                    uploaderViewController.uploadImage(selectedAudio)
                }
            }
            onClick = { uploadInput?.element?.click() }
        }
        progressBar = loadingIndicator()
        uploadedView = horizontalLayout {
            isVisible = false
            style {
                alignItems = Alignment.Center
                this["gap"] = "12px"
            }
            player = audio { controls = true }
            textView {
                text = "Delete".localized()
                style {
                    color = Color.red
                    fontSize = 13.px
                    pointerCursor()
                }
                onClick = { uploaderViewController.clearImage() }
            }
        }
    }
}

fun View?.audioUploader(defaultAudioUrl: String?): AudioUploader =
    AudioUploader(defaultAudioUrl).apply { this@audioUploader?.mount(this) }
